import { useDeferredValue, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Search, Trash2 } from "lucide-react";
import { usePagedSowingInstructionsByStatuses } from "../hooks/useSowingInstructions";
import { MarkForDiscardDialog } from "../components/sowingInstructions/MarkForDiscardDialog";
import type { SowingInstruction } from "../services/sowingInstructionsApi";
import { PaginationBar } from "@/components/ui/PaginationBar";

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StatusBadge = ({ status }: { status?: string | number }) => {
  const isReady =
    status === 5 ||
    (typeof status === "string" && status.toLowerCase().replace(/\s/g, "").includes("ready"));
  if (isReady)
    return (
      <Badge className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50">
        Ready for Dispatch
      </Badge>
    );
  return (
    <Badge className="bg-green-50 text-green-700 border-green-200 hover:bg-green-50">
      Growing
    </Badge>
  );
};

export const OvergrownDiscardPage = () => {
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selected, setSelected] = useState<SowingInstruction | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const deferredSearch = useDeferredValue(search);

  // Status 4 = Growing, Status 5 = Ready for Dispatch — both can be diseased and need discarding
  const query = usePagedSowingInstructionsByStatuses(
    [4, 5],
    pageNumber,
    pageSize,
    deferredSearch,
    false,
    false,
    false,
    true
  );
  const rows = query.data?.items ?? [];

  const handleMarkDiscard = (inst: SowingInstruction) => {
    setSelected(inst);
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    setDialogOpen(false);
    setSelected(null);
    query.refetch();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Mark Greenhouse Stock for Discard</h1>
        <p className="mt-1 text-muted-foreground">
          Growing and ready-for-dispatch stock that can be flagged for discard. Requests go to the
          Farm Manager for approval before stock is affected.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              Greenhouse Stock{" "}
              {!query.isLoading && (
                <span className="text-sm font-normal text-muted-foreground">
                  ({query.data?.totalCount ?? 0} total)
                </span>
              )}
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search by crop, variety..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPageNumber(1);
                }}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {query.isLoading && (
            <p className="py-8 text-center text-muted-foreground">Loading...</p>
          )}
          {!query.isLoading && rows.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">
              No greenhouse stock found.
            </p>
          )}
          {rows.length > 0 && (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Instruction #</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Crop Type</TableHead>
                    <TableHead>Variety</TableHead>
                    <TableHead>Qty to Sow</TableHead>
                    <TableHead>Recorded Germination</TableHead>
                    <TableHead>Greenhouse</TableHead>
                    <TableHead>Sowing Date</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((inst: SowingInstruction) => (
                    <TableRow key={inst.id}>
                      <TableCell className="font-medium text-sm">
                        {inst.instructionNumber}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={inst.status} />
                      </TableCell>
                      <TableCell>{inst.cropTypeName}</TableCell>
                      <TableCell>
                        {inst.variety ? (
                          <Badge variant="outline">{inst.variety}</Badge>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>{inst.quantityToSow}</TableCell>
                      <TableCell>
                        {inst.quantityGerminated ?? "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {inst.greenhouseName ?? "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(inst.actualSowingDate ?? inst.scheduledSowingDate)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleMarkDiscard(inst)}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Mark for Discard
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {query.data && (
            <PaginationBar
              currentPage={query.data.currentPage}
              pageSize={query.data.pageSize}
              totalCount={query.data.totalCount}
              totalPages={query.data.totalPages}
              onPageChange={setPageNumber}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageNumber(1);
              }}
            />
          )}
        </CardContent>
      </Card>

      {selected && (
        <MarkForDiscardDialog
          instruction={selected}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};
